"use client";

import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { getAnalyticsInstance } from "@/lib/firebase";

export type AnalyticsUserRole = "consumer" | "artisan" | "admin";

type AnalyticsParams = Record<string, string | number | boolean | undefined>;

const CURRENCY = "USD";

function sanitizeParams(params: AnalyticsParams): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = value;
    }
  });

  return result;
}

export async function trackEvent(
  eventName: string,
  params: AnalyticsParams = {},
) {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;

  logEvent(analytics, eventName, sanitizeParams(params));
}

export async function trackPageView(path: string, title?: string) {
  await trackEvent("page_view", {
    page_path: path,
    page_title: title,
  });
}

export async function setAnalyticsUserContext(
  userId: string,
  userRole: AnalyticsUserRole,
) {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;

  setUserId(analytics, userId);
  setUserProperties(analytics, {
    user_role: userRole,
  });
}

export async function trackBrowseMealsStarted(userRole?: AnalyticsUserRole) {
  await trackEvent("browse_meals_started", {
    flow_name: "browse_meals",
    user_role: userRole,
  });
}

export async function trackMealViewed(
  mealId: string,
  userRole?: AnalyticsUserRole,
) {
  await trackEvent("meal_viewed", {
    flow_name: "browse_meals",
    meal_id: mealId,
    user_role: userRole,
  });
}

export async function trackMealOrderStarted(
  totalValue: number,
  mealId: string | undefined,
  userRole?: AnalyticsUserRole,
) {
  await trackEvent("meal_order_started", {
    flow_name: "browse_meals",
    value: Number(totalValue.toFixed(2)),
    currency: CURRENCY,
    meal_id: mealId,
    user_role: userRole,
  });
}

export async function trackMealOrderCompleted(
  orderId: string,
  totalValue: number,
  mealId: string | undefined,
  userRole?: AnalyticsUserRole,
) {
  await trackEvent("meal_order_completed", {
    flow_name: "browse_meals",
    order_id: orderId,
    value: Number(totalValue.toFixed(2)),
    currency: CURRENCY,
    meal_id: mealId,
    user_role: userRole,
  });
}

export async function trackRegistrationCompleted(method: "email" | "google") {
  await trackEvent("registration_completed", {
    method,
    user_role: "consumer",
  });
}

export async function trackArtisanRegistrationCompleted() {
  await trackEvent("registration_completed", {
    method: "artisan_onboarding",
    flow_name: "artisan_onboarding",
    user_role: "artisan",
  });
}

export async function trackLoginSuccess(
  method: "email" | "google",
  userRole?: AnalyticsUserRole,
) {
  await trackEvent("login_success", {
    method,
    user_role: userRole,
  });
}

export async function trackMealOrderAccepted(
  orderId: string,
  totalValue: number,
  mealId: string | undefined,
) {
  await trackEvent("meal_order_accepted", {
    flow_name: "browse_meals",
    order_id: orderId,
    value: Number(totalValue.toFixed(2)),
    currency: CURRENCY,
    meal_id: mealId,
    user_role: "artisan",
  });
}